import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDataProvider, useNotify, useTranslate, SimpleForm, TextInput, SelectInput, required, maxLength, email, regex } from 'react-admin';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { Palette } from '@/themes/themeOverrides/Palette';
import { languageChoices } from '@/components/global/options/selectOptions.jsx';
import { noSpecialCharacters } from '@/components/global/validators/customValidators.jsx';
import TagCategoryToolbar from '@/components/global/tags/TagCategoryToolbar';
import CustomLoginPasswordInput from '@/components/login/CustomLoginPasswordInput.jsx';
import SaveIcon from '@/assets/icons/save-icon.svg?react';
import CancelArrow from '@/assets/icons/cancel-arrow-icon.svg?react';
import CircularProgress from '@mui/material/CircularProgress';
import { usePersistedLocale } from '@/i18n/hooks.js';

const StyledMyAccount = styled(Box)(() => ({
    '& .padding-wrapper': {
        borderTop: `2px solid ${Palette.gray.gray75}`,
        padding: '32px 56px 40px 56px',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            padding: '32px 20px 40px 20px',
        },
        width: '100%',
        boxSizing: 'border-box',
        '& h5': {
            marginBottom: '32px',
            fontSize: '22px',
            letterSpacing: '-0.44px',
        },
        '& .MuiFormHelperText-root': {
            display: 'none',
        },
        '& .Mui-error + .MuiFormHelperText-root': {
            display: 'block',
        },
        '& .MuiFormControl-root': {
            marginBottom: '32px',
            marginTop: '0',
            '&:last-child': {
                marginBottom: '0',
            },
            '&.ra-input-newPassword, &.ra-input-repeatNewPassword': {
                minWidth: '276px',
            },
        },
    },
}));

const UserFormToolbar = ({ handleClose = () => {} }) => {
    const translate = useTranslate();
    return (
        <Box className='padding-wrapper popup-wrapper-bottom'>
            <TagCategoryToolbar
                onCancel={handleClose}
                cancelLabel={translate('common.cancel')}
                cancelIcon={<CancelArrow />}
                saveLabel={translate('common.save')}
                saveIcon={<SaveIcon />}
            />
        </Box>
    );
};

UserFormToolbar.propTypes = {
    handleClose: PropTypes.func,
};

const USER_ACCOUNT_QUERY_KEY = ['user-account'];

export const MyAccount = ({ handleClose = () => {} }) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const queryClient = useQueryClient();
    const [, setLocale] = usePersistedLocale();
    const validatePhone = regex(/^(\+48)[1-9]\d{8}$/, translate('resources.user.validation.invalidPhone'));

    const {
        data = {},
        isPending: loading,
        error,
    } = useQuery({
        queryKey: USER_ACCOUNT_QUERY_KEY,
        queryFn: () => dataProvider.getRaw('/user-account').then(response => response.data ?? {}),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    useEffect(() => {
        if (!error) {
            return;
        }
        const message = error?.body?.message || error?.message || translate('common.errors.loadingData');
        notify(message, { type: 'error' });
    }, [error, notify, translate]);

    const handleSubmit = values => {
        const emailChanged = values.email && values.email !== data.email;
        const languageChanged = values.language && values.language !== data.language;

        const payload = { ...values };

        if (payload.newPassword || payload.repeatNewPassword) {
            if (payload.newPassword !== payload.repeatNewPassword) {
                notify(translate('account.myAccount.validation.passwordsMustMatch'), { type: 'error' });
                return;
            }
        }

        if (payload.newPassword) {
            payload.password = payload.newPassword;
        }
        delete payload.newPassword;
        delete payload.repeatNewPassword;

        dataProvider
            .put('user-account', { data: payload })
            .then(() => {
                if (emailChanged) {
                    notify(translate('account.myAccount.notifications.emailConfirmationSent'), { type: 'info' });
                } else {
                    notify(translate('account.myAccount.notifications.success'), { type: 'success' });
                }
                handleClose();
                queryClient.removeQueries({ queryKey: USER_ACCOUNT_QUERY_KEY });
                if (languageChanged) {
                    setLocale(values.language);
                }
            })
            .catch(error => {
                const message = error?.body?.message || error?.message || translate('common.errors.updateFailed');
                notify(message, { type: 'error' });
            });
    };

    return (
        <StyledMyAccount>
            <Box className='popup-wrapper popup-wrapper-top'>
                <Box className='popup-box popup-box--header'>
                    <Typography variant='h4'>{translate('account.myAccount.title')}</Typography>
                    <Box>
                        <CustomCloseButton className='close-button' onClose={handleClose} />
                    </Box>
                </Box>
            </Box>
            {(loading && <CircularProgress size={20} sx={{ margin: '20px 56px' }} />) || (
                <SimpleForm className='user-account-form' defaultValues={data} toolbar={<UserFormToolbar handleClose={handleClose} />} onSubmit={handleSubmit}>
                    <Box className='padding-wrapper'>
                        <Typography variant='h5' className='section-header'>
                            {translate('account.myAccount.sections.basicInfo')}
                        </Typography>
                        <TextInput
                            source='firstName'
                            label={translate('account.myAccount.fields.firstName')}
                            validate={[
                                required(translate('common.validation.required')),
                                maxLength(150, translate('common.validation.maxLength', { count: 150 })),
                                noSpecialCharacters,
                            ]}
                            fullWidth
                        />
                        <TextInput
                            source='lastName'
                            label={translate('account.myAccount.fields.lastName')}
                            validate={[
                                required(translate('common.validation.required')),
                                maxLength(150, translate('common.validation.maxLength', { count: 150 })),
                                noSpecialCharacters,
                            ]}
                            fullWidth
                        />
                        <TextInput
                            source='email'
                            type='email'
                            label={translate('account.myAccount.fields.email')}
                            validate={[
                                required(translate('common.validation.required')),
                                email(translate('common.validation.invalidEmail')),
                                maxLength(100, translate('common.validation.maxLength', { count: 100 })),
                            ]}
                            fullWidth
                        />
                        <TextInput source='phone' label={translate('account.myAccount.fields.phone')} fullWidth validate={validatePhone} />
                    </Box>
                    <Box className='padding-wrapper'>
                        <Typography variant='h5' className='section-header'>
                            {translate('account.myAccount.sections.passwordChange')}
                        </Typography>
                        <CustomLoginPasswordInput
                            source='newPassword'
                            label={translate('account.myAccount.fields.newPassword')}
                            isRequired={false}
                            disableAutoComplete
                            fullWidth
                        />
                        <CustomLoginPasswordInput
                            source='repeatNewPassword'
                            label={translate('account.myAccount.fields.repeatNewPassword')}
                            isRequired={false}
                            disableAutoComplete
                            fullWidth
                        />
                    </Box>

                    <Box className='padding-wrapper'>
                        <Typography variant='h5' className='section-header'>
                            {translate('account.myAccount.sections.language')}
                        </Typography>
                        <SelectInput
                            source='language'
                            label={translate('resources.user.labels.languageSelect')}
                            choices={languageChoices(translate)}
                            validate={required(translate('common.validation.required'))}
                            fullWidth
                        />
                    </Box>
                </SimpleForm>
            )}
        </StyledMyAccount>
    );
};

MyAccount.propTypes = {
    handleClose: PropTypes.func,
};
